<template>
    <div :data-type="type" class="form-item-select-input">
        <div class="d-flex flex-row">
            <label v-if="enumeration != ''" class="text-color-number mb-1">
                {{ enumeration }}
            </label>
            <label :for="'form-global-' + fieldId" class="mb-1 ps-2 text-select-label text-color-questions">
                <span v-if="required">* </span>{{ label }}
            </label>
        </div>
        <div class="form-input-help mb-1"
             v-if="helpText">{{ helpText }}</div>
        <select
            :id="'form-global-' + typeFieldId + '_' + fieldId"
            :class="[inputClass, { 'is-invalid': shouldShowError }]"
            :name="name"
            :value="selectedValue"
            @change="handleSelectChange">

            <option value="" disabled>
                {{ placeholder }}
            </option>

            <option
                v-for="(label, optionValue) in options"
                :key="optionValue"
                :value="optionValue">
                {{ label }}
            </option>
        </select>
        <!-- Campo de texto para "other" -->
        <div v-if="isOtherSelected" class="mt-2">
            <input
                type="text"
                class="form-control"
                :placeholder="otherText || 'Other...'"
                v-model="otherValue"
                :id="'form-global-' + fieldId"
                :name="'question_' + typeFieldId + '_' + fieldId + '_' + otherId + '_text_other'"
                @input="handleOtherTextChange($event.target.value)" />
        </div>
        <div v-if="shouldShowError" class="has-error-msg invalid-feedback d-block">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script setup>
import { computed, watch, ref, onMounted } from 'vue';
import { useField } from 'vee-validate';
import { useTranslation } from '@/composables/useTranslation';

// INITIALIZE STORE & COMPOSABLES
const { translate } = useTranslation();

// PROPS & EMITS
const props = defineProps({
    enumeration: {
        type: String,
        required: false,
        default: '',
    },
    errorText: {
        type: String,
        required: false,
    },
    fieldId: {
        type: Number,
        required: true,
    },
    helpText: {
        type: String,
        required: false,
    },
    inputClass: {
        type: String,
        required: false,
    },
    label: {
        type: String,
        required: true,
    },
    labelClass: {
        type: String,
        required: false,
    },
    modelAnswer: {
        type: String,
        required: false,
    },
    name: {
        type: String,
        required: true,
    },
    options: {
        type: Object,
        required: true,
    },
    otherId: {
        type: Number,
        required: false,
    },
    otherText: {
        type: String,
        required: false,
    },
    question: {
        type: Object,
        required: true,
    },
    required: {
        type: Number,
        required: false,
    },
    textSelect: {
        type: String,
        required: true,
    },
    type: {
        type: String,
        required: true,
    },
    typeFieldId: { type: Number, required: true },
    validation: {
        type: Object,
        required: false,
    },
    hasSubmitted: {
        type: Boolean,
        default: false
    },
});

const emit = defineEmits(['update:modelAnswer', 'register-field', 'field-change', 'other-selected']);

// REACTIVE VARIABLES
const selectedValue = ref('');
const otherValue = ref('');
const previousOtherValue = ref('');

// COMPUTED PROPERTIES
const validationRules = computed(() => {
    return (value) => {
        if (props.validation.required && (!value || value.toString().trim() === '')) {
            return props.errorText || translate('label.this_value_is_required');
        }

        return true;
    };
});

const placeholder = computed(() => {
    if (props.question.placeholder === true) {
        return props.question.description;
    }
    return props.textSelect;
});


// Solo mostrar errores después del primer submit
const shouldShowError = computed(() => {
    return props.hasSubmitted && errorMessage.value;
});

// Verificar si "other" está seleccionado
const isOtherSelected = computed(() => {
    return selectedValue.value === 'other';
});

// METHODS
const handleSelectChange = (event) => {
    const optionValue = event.target.value;
    selectedValue.value = optionValue;

    if (optionValue === 'other') {
        // Restaurar el valor previo de "other" si existe
        otherValue.value = previousOtherValue.value;
        const finalValue = otherValue.value || '';
        value.value = finalValue;
        emit('update:modelAnswer', 'other');
        emit('field-change', props.name, 'other');

        // Emitir evento para datos de "other"
        emit('other-selected', {
            optionId: props.otherId,
            otherValue: finalValue,
            isSelected: true
        });
    } else {
        // Guardar el texto actual de "other" antes de cambiar
        if (otherValue.value) {
            previousOtherValue.value = otherValue.value;
        }

        // Limpiar el campo other visualmente pero mantener el valor guardado
        otherValue.value = '';
        value.value = optionValue;
        emit('update:modelAnswer', optionValue);
        emit('field-change', props.name, optionValue);

        // Emitir evento indicando que "other" ya no está seleccionado
        emit('other-selected', {
            optionId: props.otherId,
            otherValue: '',
            isSelected: false
        });
    }
};

// Manejar cambios en el campo "other"
const handleOtherTextChange = (newText) => {
    otherValue.value = newText;
    previousOtherValue.value = newText;

    // Si "other" está seleccionado, actualizar el valor
    if (selectedValue.value === 'other') {
        value.value = newText;
        emit('update:modelAnswer', 'other');
        emit('field-change', props.name, 'other');

        // Emitir evento actualizado para datos de "other"
        emit('other-selected', {
            optionId: props.otherId,
            otherValue: newText,
            isSelected: true
        });
    }
};
// Configurar useField con validación lazy
const { value, errorMessage, meta } = useField(props.name, validationRules, {
    initialValue: props.question.answer || '',
    validateOnValueUpdate: false,
    validateOnMount: false,
});

// LIFECYCLE HOOKS
// Sincronizar con el valor externo
watch(() => props.question.answer, (newValue) => {
    const stringValue = newValue ? newValue.toString() : '';

    // Verificar si el valor corresponde a una de las opciones predefinidas
    const isKnownOption = Object.keys(props.options).includes(stringValue);

    if (isKnownOption) {
        selectedValue.value = stringValue;
        value.value = stringValue;
    } else if (stringValue) {
        // Si no es una opción conocida, es texto de "other"
        selectedValue.value = 'other';
        otherValue.value = stringValue;
        previousOtherValue.value = stringValue;
        value.value = stringValue;
    } else {
        selectedValue.value = '';
        otherValue.value = '';
        previousOtherValue.value = '';
        value.value = '';
    }
}, { immediate: true });

watch(() => props.question.answerOther, (newValue) => {
    otherValue.value = newValue || '';
}, { immediate: true });



// Registrar campo al montar
onMounted(() => {
    emit('register-field', props.name, selectedValue.value);
});
</script>
